import React, {useEffect, useState} from "react";
import {searchThemes} from "../../../theme/services/theme-data-service";
import QuoteList from "../../../quote/quote-list";
import DesignInfoFilters from "../../../shopify/components/design-info/design-info-filters";
import {DESIGN_INFO_FILTER_FIELDS_FOR_PRODUCTS} from "../../../shopify/constants/design-info.constant";
import ShowProgressSpinner from "../../../common/ui/show-progress-spinner";
import ShowEmptyListMessage from "../../../design/components/common/show-empty-list-message";
import {TrashIcon} from "@heroicons/react/24/solid";
import {handleFieldValueChanges} from "../../../common/utils/field-change-util";
import {getSingleQuote, searchQuotesWithSearchCriteria} from "../../../common/services/quote-data-service";
import _ from "lodash";

const defaultFilterCriteria: any = {};

const ThemesQuoteDashboard = () => {
    const [initialStep, setInitialStep] = useState(true);
    const [selectedQuote, setSelectedQuote] = useState(null);
    const [allThemes, setAllThemes] = useState([]);
    const [dataFetchInProgress, setDataFetchInProgress] = useState(true);
    const [filterCriteria, setFilterCriteria] = useState(defaultFilterCriteria);
    const [quotesList, setQuotesList] = useState([]);

    useEffect(() => {
        getThemesList();
        getQuoteList({searchText: '', themeId: ''});
    }, [initialStep]);

    const getThemesList = async () => {
        const themesListResult = await searchThemes('')
        setAllThemes(_.orderBy(themesListResult, ['startText'], ['asc']));
    }

    const handleDetailChanges = (event) => {
        const existingFilter = {...filterCriteria};

        const {name, value} = event.target;
        existingFilter[name] = value;
        setFilterCriteria(existingFilter);

        getQuoteList(existingFilter);
    }

    const handleClearSearch = () => {
        const newFilter = {searchText: '', themeId: ''};
        setFilterCriteria(newFilter);
        getQuoteList(newFilter);
    }

    const getQuoteList = async (existingFilter) => {
        setDataFetchInProgress(true);

        let quotesListResult = await searchQuotesWithSearchCriteria(prepareSearchCriteria(existingFilter));
        quotesListResult = _.orderBy(quotesListResult, ['startText'], ['asc']);
        setQuotesList(quotesListResult);
        setSelectedQuote(quotesListResult[0]);

        setDataFetchInProgress(false);
    }

    const prepareSearchCriteria = (existingFilter) => {
        const searchCriteriaList = [];

        if (!_.isEmpty(existingFilter?.themeId)) {
            searchCriteriaList.push({field: 'themes.' + existingFilter?.themeId, condition: '==', value: true});
        }

        if (!_.isEmpty(existingFilter?.searchText)) {
            searchCriteriaList.push({
                field: 'textList',
                condition: 'array-contains',
                value: _.toLower(existingFilter?.searchText)
            });
        }

        return searchCriteriaList;
    }

    const handleSelectQuote = async (quote) => {
        const existingQuote = await getSingleQuote(quote.id);
        const quoteData = existingQuote.data();
        quoteData.id = existingQuote.id;

        setSelectedQuote(quoteData);
    }

    return (
        <>

            <div className="flex justify-between">

                <div className="flex justify-start items-center -ml-2 w-full">
                    <div className={'text-xl ml-2 w-48'}>
                        Themes & Quotes
                    </div>

                    <div className="flex justify-between">
                        <div className="field-container">
                            <select
                                className="input-field h-12 -ml-2"
                                id="themeId"
                                name="themeId"
                                value={filterCriteria?.themeId}
                                onChange={handleDetailChanges}>
                                <option value="">Choose Theme</option>
                                {allThemes?.map((theme, index) => (
                                    <option key={index}
                                            value={theme?.id}>{theme?.startText}</option>
                                ))}
                            </select>
                        </div>

                        <div className="field-container">
                            <input type="text" placeholder="Search"
                                   id="searchText"
                                   name="searchText"
                                   value={filterCriteria?.searchText}
                                   onChange={handleDetailChanges}
                                   className="h-12 w-full border border-gray-500 p-4 rounded mr-2"/>
                        </div>

                        <div className="field-container">
                            <button
                                onClick={() => handleClearSearch()}
                                className="rounded flex items-center justify-center border-gray-500 border bg-white text-gray-500 h-12 hover:bg-gray-500 hover:text-white">
                                <div className="p-4">
                                    <TrashIcon className="h-6 w-6"/>
                                </div>
                            </button>
                        </div>
                    </div>

                    <div className="ml-4">
                        <ShowProgressSpinner dataFetchInProgress={dataFetchInProgress}/>
                    </div>
                </div>

            </div>

            <div className="flex justify-start mt-4">

                <div className="w-1/4">

                    <div
                        className={'table-container-with-title-2 bg-white overflow-y-scroll border border-gray-400 rounded-lg'}>

                        <ShowEmptyListMessage
                            dataList={allThemes}
                            dataFetchInProgress={false}
                            label={'Themes'}/>

                        {quotesList?.length > 0 &&
                            <table className="w-full">
                                <thead className="">
                                <tr>
                                    <th className="p-2 border-y border-gray-400 bg-gray-400 text-white sticky top-0 w-8">S.No</th>
                                    <th className="p-2 border-y border-gray-400 bg-gray-400 text-white sticky top-0 text-left">Name</th>
                                </tr>
                                </thead>
                                <tbody className="">
                                {quotesList?.map((quote, rowIndex) => (
                                    <tr key={`row-${rowIndex}`}
                                        onClick={() => {
                                            handleSelectQuote(quote)
                                        }}
                                        className={`border-b border-gray-400 cursor-pointer hover:bg-green-100 ${(selectedQuote && selectedQuote?.id === quote?.id) ? 'bg-green-200' : ''}`}>
                                        <td className="p-2 m-auto">
                                            <div className="flex justify-center w-8">
                                                {rowIndex + 1}
                                            </div>
                                        </td>

                                        <td className={`p-2 m-auto flex justify-start items-center`}>
                                            <div className="">
                                                {quote.startText}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        }

                    </div>

                </div>

                <div className={'w-3/4'}>
                    <div className={'mx-4'}>
                        <ShowEmptyListMessage
                            dataList={selectedQuote ? [selectedQuote] : []}
                            dataFetchInProgress={dataFetchInProgress}
                            label={'Quotes'}/>

                        {selectedQuote &&
                            <>
                            </>
                        }
                    </div>
                </div>

            </div>

        </>
    )
}

export default ThemesQuoteDashboard
